import React, { useMemo, useState } from 'react'
import { NotFound, Tabs } from 'components'
import { _, validation } from 'utils'
import * as Yup from 'yup'
import { useParams } from 'react-router'
import Settings from './tabs/Settings'
import Participants from './tabs/Participants'
import { useEventSlug, useQuery } from '../../hooks'
import {
    GroupSessionsQuery,
    GroupSessionsQueryVariables, GroupSessionType, UserType,
} from '../../generated/graphql'
import { groupSessionsQuery } from './graphql/query'
import { getNodes } from '../../graphql/utils'
import FullPageLoading from '../../components/FullPageLoading'

export const GroupSessionValidation = Yup.object().shape({
    title: Yup.string().required().min(3),
    startsAt: Yup.date().required(),
    endsAt: validation.endsAt,
})

interface GroupSession extends Omit<GroupSessionType, 'participants'|'enableHostChat'> {
    participants: Partial<UserType>[]
}

type GroupSessionContextValue = {
    groupSession: GroupSession
    refresh: () => void
}
export const GroupSessionContext = React.createContext<GroupSessionContextValue>({
    groupSession: undefined,
    refresh: () => {},
})

const GroupSessionEditor = () => {
    const eventSlug = useEventSlug()
    const [selectedTab, setSelectedTab] = useState<string>()
    const { id: groupSessionId } = useParams<{ id: string }>()

    const {
        data: groupSessionNodes,
        requestStatus,
        fetchData
    } = useQuery<GroupSessionsQuery, GroupSessionsQueryVariables>(groupSessionsQuery, {
        eventId: eventSlug, first: 1, filters: { id: +groupSessionId }
    })

    const groupSession = useMemo(() => {
        if (!groupSessionNodes || requestStatus !== 'succeeded') {
            return
        }
        const groupSessionNode = getNodes(groupSessionNodes.event.groupSessions)[0]
        return {
            ...groupSessionNode,
            participants: getNodes(groupSessionNode.participants),
        }
    }, [groupSessionNodes, requestStatus])

    const groupSessionContextValue = useMemo(() => ({
        groupSession,
        refresh: fetchData
    }), [fetchData, groupSession])

    if (!groupSession || requestStatus === 'loading') {
        return <FullPageLoading />
    }

    if (requestStatus === 'failed') {
        return <NotFound />
    }

    return (
        // @ts-ignore
        <GroupSessionContext.Provider value={groupSessionContextValue}>
            <Tabs
                onSelect={setSelectedTab}
                defaultActiveKey={selectedTab || 'settings'}
                tabs={[
                    { id: 'settings', name: _('groupsession.tabs.settings'), Component: Settings },
                    {
                        id: 'participants',
                        name: _('groupsession.tabs.participants'),
                        Component: Participants
                    },
                ]}
            />
        </GroupSessionContext.Provider>
    )
}

export default GroupSessionEditor
